import React from "react";
import {
  TextField,
  Typography,
  Box,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller } from "react-hook-form";
import PropTypes from "prop-types";

const BlanketDetailedInformationBase = ({ config, control, errors, watch }) => {
  const { title } = config;

  return (
    <Box className="grid grid-cols-1 gap-4" data-testid="bond-wizard-step-detailed">
      <Typography variant="h5" className="font-bold mb-4">
        {title}
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography>Have you been bankrupt or insolvent?</Typography>
          <Controller
            name="bankrupt"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("bankrupt") === "Yes" && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="bankruptExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation (Year)"
                  fullWidth
                  variant="outlined"
                  error={!!errors.bankruptExplanation}
                  helperText={errors.bankruptExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>
            Any lawsuits, judgments, or liens against you?
          </Typography>
          <Controller
            name="lawsuits"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("lawsuits") === "Yes" && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="lawsuitsExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation"
                  fullWidth
                  variant="outlined"
                  error={!!errors.lawsuitsExplanation}
                  helperText={errors.lawsuitsExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>
            Has application for this bond been declined by another surety?
          </Typography>
          <Controller
            name="declinedSurety"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("declinedSurety") === "Yes" && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="suretyExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation"
                  fullWidth
                  variant="outlined"
                  error={!!errors.suretyExplanation}
                  helperText={errors.suretyExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>Is this bond replacing a previous bond?</Typography>
          <Controller
            name="replacingBond"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("replacingBond") === "Yes" && (
          <>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="bondReplacementReason"
                control={control}
                defaultValue=""
                rules={{ required: "Explanation is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Provide surety name and reason for change"
                    fullWidth
                    variant="outlined"
                    error={!!errors.bondReplacementReason}
                    helperText={errors.bondReplacementReason?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="doe"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date of Expiration of Current Bond"
                    type="date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12 }}>
          <Typography>Which bond term would you like?</Typography>
          <Controller
            name="bondTerm"
            control={control}
            defaultValue="1 Year Bond"
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel
                  value="1 Year Bond"
                  control={<Radio />}
                  label={
                    <>
                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                        $175.00
                      </Typography>{" "}
                      (1 Year Bond)
                    </>
                  }
                />
                <FormControlLabel
                  value="2 Year Bond"
                  control={<Radio />}
                  label={
                    <>
                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                        $306.00
                      </Typography>{" "}
                      (2 Year Bond)
                    </>
                  }
                />
                <FormControlLabel
                  value="3 Year Bond"
                  control={<Radio />}
                  label={
                    <>
                      <Typography component="span" sx={{ fontWeight: "bold" }}>
                        $437.00
                      </Typography>{" "}
                      (3 Year Bond)
                    </>
                  }
                />
              </RadioGroup>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

BlanketDetailedInformationBase.propTypes = {
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  watch: PropTypes.func.isRequired,
};

export default BlanketDetailedInformationBase;
